import { RenderAdapter } from './render-adapter.js';

/**
 * CanvasAdapter - Render adapter for canvas/WebGL apps (games, visualizations).
 *
 * Chain-aware: each chain level can have its own view. The adapter tracks
 * per-level state and only updates levels that actually changed.
 *
 * A level's `view` arrives from the server as the PATH of its `views/<component>.js` module.
 * The adapter imports it once (the browser module map caches it for the page's lifetime) and
 * hands the renderer the module's default export: a function `(data) => description`, or a
 * plain description. The renderer evaluates a function view with the level's controller and may
 * do so again whenever the controller's data changes (live updates re-render from the cached
 * module; only data travels).
 *
 * The project provides a "renderer" object that implements:
 *   - init(data, ssr) — bootstrap with initial data
 *   - setScene(controller, response, chainIndex) — add/replace scene for a chain level;
 *     response = { view, data, chain } with view = function or description
 *   - removeScene(chainIndex) — remove scene for a chain level
 *   - clearScenes() — remove all chain level scenes
 *   - restart() — optional, reload current scene
 *   - leave(nextController) — optional cleanup
 */
const viewModules = new Map();
async function importView(path) {
	if (!viewModules.has(path)) viewModules.set(path, import('/crates' + path).then(m => m.default));
	return viewModules.get(path);
}

export class CanvasAdapter extends RenderAdapter {
	constructor(renderer) {
		super();
		this.renderer = renderer;
		this.chain = null;
	}

	resolveViewPath(component, viewsDir) {
		return `${viewsDir}/${component}.js`;
	}

	async init(ssr) {
		if (this.renderer.init) {
			await this.renderer.init(ssr.data, ssr);
		}

		// Process ALL chain levels, not just the leaf
		if (ssr.chain) {
			for (let i = 0; i < ssr.chain.length; i++) {
				const level = ssr.chain[i];
				if (!level.view) continue;

				let controller = level.data || {};
				if (level.frontendHandler) {
					controller = await importFrontendHandler(level.frontendHandler, level.data);
				}

				if (this.renderer.setScene) {
					await this.renderer.setScene(controller, { view: await importView(level.view), data: level.data, chain: ssr.chain }, i);
				}
			}
			this.chain = ssr.chain;
		}

		if (ssr.tracking) {
			const leaf = ssr.chain?.[ssr.chain.length - 1];
			webSocket.setTracking({
				path: location.pathname,
				crate: leaf?.crate,
				...ssr.tracking,
			});
		}

		return null;
	}

	async update(response, controller) {
		// Navigation guarantees: same chain length, same wrappers — only leaf changed
		const chain = response.chain;
		if (chain && this.chain) {
			const leafIdx = chain.length - 1;

			// Remove old leaf
			if (this.renderer.removeScene) {
				await this.renderer.removeScene(leafIdx);
			}

			// Build new leaf
			if (chain[leafIdx]?.view && this.renderer.setScene) {
				await this.renderer.setScene(controller, { view: await importView(chain[leafIdx].view), data: chain[leafIdx].data, chain }, leafIdx);
			}
		} else if (this.renderer.setScene) {
			// Fallback: no chain info
			await this.renderer.setScene(controller, response, 0);
		}

		this.chain = chain || this.chain;

		if (response.tracking) {
			webSocket.setTracking({
				path: location.pathname,
				crate: response.chain?.[response.chain.length - 1]?.crate,
				...response.tracking,
			});
		}
	}

	async replaceChain(response, controller) {
		const chain = response.chain;
		const oldChain = this.chain;

		if (chain && oldChain) {
			// Remove excess old levels (chain got shorter)
			for (let i = oldChain.length - 1; i >= chain.length; i--) {
				if (this.renderer.removeScene) {
					await this.renderer.removeScene(i);
				}
			}

			// Process each level in new chain
			for (let i = 0; i < chain.length; i++) {
				const level = chain[i];
				const oldLevel = oldChain[i];
				const isLeaf = i === chain.length - 1;

				// Skip unchanged wrapper levels (same crate+component, no new view sent)
				const same = oldLevel
					&& oldLevel.crate === level.crate
					&& oldLevel.component === level.component;

				if (same && !level.view && !isLeaf) {
					continue;
				}

				// Remove old level if it existed
				if (oldLevel && this.renderer.removeScene) {
					await this.renderer.removeScene(i);
				}

				// Build new level
				if (level.view) {
					let ctrl;
					if (isLeaf) {
						ctrl = controller;
					} else {
						ctrl = level.data || {};
						if (level.frontendHandler) {
							ctrl = await importFrontendHandler(level.frontendHandler, level.data);
						}
					}
					if (this.renderer.setScene) {
						await this.renderer.setScene(ctrl, { view: await importView(level.view), data: level.data, chain }, i);
					}
				}
			}
		} else if (chain) {
			// No old chain — build everything fresh
			if (this.renderer.clearScenes) {
				await this.renderer.clearScenes();
			}
			for (let i = 0; i < chain.length; i++) {
				const level = chain[i];
				if (!level.view) continue;

				const isLeaf = i === chain.length - 1;
				let ctrl;
				if (isLeaf) {
					ctrl = controller;
				} else {
					ctrl = level.data || {};
					if (level.frontendHandler) {
						ctrl = await importFrontendHandler(level.frontendHandler, level.data);
					}
				}
				if (this.renderer.setScene) {
					await this.renderer.setScene(ctrl, { view: await importView(level.view), data: level.data, chain }, i);
				}
			}
		}

		this.chain = chain || this.chain;

		if (response.tracking) {
			webSocket.setTracking({
				path: location.pathname,
				crate: response.chain?.[response.chain.length - 1]?.crate,
				...response.tracking,
			});
		}
	}

	async reload() {
		if (this.renderer.restart) {
			await this.renderer.restart();
		}
	}

	async leave(nextController) {
		if (this.renderer.leave) {
			await this.renderer.leave(nextController);
		}
	}
}

export default CanvasAdapter;
